/**
 * Loyalty points at a basket checkout, as both basket checkouts offer them:
 * the widget's full-page checkout and the webcomponents overlay Website v2
 * checks out through. The buyer only opts in (`redeemPoints`); the server
 * recomputes every figure, requires a verified sign-in whose email matches the
 * buyer, and refuses points on a request or a deposit basket.
 */

/** `GET /widget/loyalty/me?schoolId=` — the signed-in customer's standing. */
export type LoyaltySummary = {
  programStatus: "ACTIVE" | "PAUSED";
  pointsBalance: number;
  pointsValuePence: number;
  minRedeemPoints: number;
  redemptionPencePerPoint: number;
  tier: { id: string; name: string; perkPercentOff: number | null } | null;
  nextTier: {
    name: string;
    thresholdPence: number;
    remainingPence: number;
  } | null;
  lifetimeSpendPence: number;
};

/** What the checkout block offers: spend points, earn N more before you
 *  can, or nothing. */
export type LoyaltyRedeemState =
  | { kind: "redeem"; maxValuePence: number }
  | { kind: "earn-more"; pointsNeeded: number }
  | { kind: "none" };

export function loyaltyRedeemState(summary: LoyaltySummary): LoyaltyRedeemState {
  const active = summary.programStatus === "ACTIVE";
  if (
    active &&
    summary.pointsBalance > 0 &&
    summary.pointsBalance >= summary.minRedeemPoints
  ) {
    return { kind: "redeem", maxValuePence: summary.pointsValuePence };
  }
  if (active && summary.pointsBalance < summary.minRedeemPoints) {
    return {
      kind: "earn-more",
      pointsNeeded: summary.minRedeemPoints - Math.max(0, summary.pointsBalance),
    };
  }
  return { kind: "none" };
}

/**
 * The `redeemPoints` checkout field. Never on a request (no money is
 * collected) or a basket with a deposit item (the server answers 400
 * LOYALTY_POINTS_DEPOSIT_UNSUPPORTED), whatever the toggle says.
 */
export function redeemPointsForPayload({
  redeemPoints,
  isRequestToBook,
  hasDepositItem,
}: {
  redeemPoints: boolean;
  isRequestToBook: boolean;
  hasDepositItem: boolean;
}): boolean {
  return redeemPoints && !isRequestToBook && !hasDepositItem;
}

/** "£1.23" from pence and a currency symbol, as the loyalty block shows it. */
export function formatLoyaltyMoney(pence: number, currencySymbol = "£"): string {
  return `${currencySymbol}${(pence / 100).toFixed(2)}`;
}
